import type { ClientMessage, ServerMessage } from '../shared/messages';
import { GAME_SERVER_URL } from './config';

type MessageHandler = (message: ServerMessage) => void;
type SimpleHandler = () => void;
type CloseHandler = (code: number, reason: string) => void;

function socketBase(): { protocol: string; host: string } {
  if (GAME_SERVER_URL) {
    try {
      const target = new URL(GAME_SERVER_URL);
      return {
        protocol: target.protocol === 'https:' ? 'wss:' : 'ws:',
        host: target.host,
      };
    } catch {
      // fall through to same-origin
    }
  }
  return {
    protocol: location.protocol === 'https:' ? 'wss:' : 'ws:',
    host: location.host,
  };
}

export function wsUrl(params: string): string {
  const { protocol, host } = socketBase();
  return `${protocol}//${host}/ws?${params}`;
}

export function wsDiagUrl(): string {
  const { protocol, host } = socketBase();
  return `${protocol}//${host}/ws-diag`;
}

export class GameSocket {
  private socket: WebSocket | null = null;
  private closedByUser = false;
  readonly onMessage: MessageHandler;
  readonly onOpen: SimpleHandler;
  readonly onClose: CloseHandler;

  constructor(
    private readonly url: string,
    handlers: {
      onMessage: MessageHandler;
      onOpen?: SimpleHandler;
      onClose?: CloseHandler;
    },
  ) {
    this.onMessage = handlers.onMessage;
    this.onOpen = handlers.onOpen ?? (() => {});
    this.onClose = handlers.onClose ?? (() => {});
  }

  connect(): void {
    const socket = new WebSocket(this.url);
    this.socket = socket;
    socket.addEventListener('open', () => this.onOpen());
    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        if (message && typeof message.type === 'string') this.onMessage(message);
      } catch {
        // ignore malformed frames
      }
    });
    socket.addEventListener('close', (event) => {
      if (!this.closedByUser) this.onClose(event.code, event.reason);
    });
    socket.addEventListener('error', () => {
      // close follows
    });
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  send(message: ClientMessage): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  close(): void {
    this.closedByUser = true;
    try {
      this.socket?.close(1000, 'CLIENT_LEAVE');
    } catch {
      // already closed
    }
    this.socket = null;
  }
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle]!;
  return (sorted[middle - 1]! + sorted[middle]!) / 2;
}

export class ClockSync {
  private samples: number[] = [];
  private seeded = false;
  offsetMs = 0;
  rttMs = 80;

  seed(serverTime: number, localNow = Date.now()): void {
    if (this.seeded) return;
    this.offsetMs = serverTime - localNow;
    this.seeded = true;
  }

  onPong(t: number, serverTime: number, localNow = Date.now()): number {
    const rtt = Math.max(0, Math.min(2000, localNow - t));
    const offset = serverTime - (t + rtt / 2);
    this.samples.push(offset);
    if (this.samples.length > 9) this.samples.shift();
    this.offsetMs = median(this.samples);
    this.rttMs = Math.round(rtt);
    this.seeded = true;
    return this.rttMs;
  }

  get isSeeded(): boolean {
    return this.seeded;
  }

  toLocal(serverTime: number): number {
    return serverTime - this.offsetMs;
  }

  nowServer(): number {
    return Date.now() + this.offsetMs;
  }
}
